import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterParklands() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-parklands",
    name: "Painters Parklands",
    location: "Parklands, Cape Town",
    rating: 4.7,
    reviews: 134,
    specialties: ["Residential", "Commercial", "Estate Painting", "Modern Homes"],
    image: "https://public.readdy.ai/ai/img_res/7015f2c42273833f735b9ddf2d05f1a9.jpg",
    verified: true,
    yearsExperience: 9,
    website: "https://paintersparklands.co.za",
    email: "info@paintersparklands.co.za",
    description: "Reliable painting contractors in Parklands and northern suburbs. Specializing in residential estates, commercial properties, and modern homes with quality finishes.",
    services: [
      "Residential Estate Painting",
      "Modern Home Interior Painting", 
      "Commercial Property Painting",
      "Townhouse Complex Painting",
      "Apartment Building Painting",
      "Office and Retail Painting",
      "Body Corporate Painting Services",
      "Maintenance and Touch-up Services"
    ],
    serviceAreas: ["Parklands", "Table View", "Bloubergstrand", "Milnerton", "Northern Suburbs", "Cape Town", "Western Cape"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20modern%20home%20interior%20painted%20by%20professional%20painters%20in%20Parklands%2C%20contemporary%20residential%20estate%20with%20fresh%20painted%20walls%2C%20quality%20paint%20finish%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=parklands-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Residential%20estate%20painting%20project%20in%20Parklands%20Cape%20Town%2C%20modern%20townhouse%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20suburban%20setting%2C%20well-maintained%20property&width=400&height=300&seq=parklands-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Commercial%20property%20painting%20project%20in%20Parklands%2C%20professional%20painters%20working%20on%20office%20building%20exterior%2C%20quality%20commercial%20paint%20finish%2C%20modern%20business%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=parklands-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Apartment%20complex%20painting%20in%20Parklands%20residential%20estate%2C%20fresh%20painted%20building%20exterior%2C%20quality%20paint%20finish%2C%20bright%20modern%20development%2C%20professional%20painting%20work&width=400&height=300&seq=parklands-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Susan Mitchell",
        rating: 5,
        comment: "Excellent work on our Parklands townhouse. The painters were professional, efficient, and delivered a beautiful finish that has transformed our home. Highly recommended for estate properties."
      },
      {
        name: "Robert Johnson", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our commercial property painting on schedule and the quality exceeded our expectations. Great value for money."
      },
      {
        name: "Linda van Zyl",
        rating: 4,
        comment: "Very reliable team and excellent attention to detail. Our apartment complex looks fantastic and the body corporate was very pleased with the professional approach."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Parklands | Professional House Painting Services Cape Town | Residential Painters";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Parklands offers professional painting services in Parklands, Cape Town. Specializing in residential painting, modern properties, interior and exterior work. Trusted local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Parklands, house painters Parklands, painting contractors Cape Town, residential painting, modern property painting');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20modern%20residential%20estate%20in%20Parklands%20Cape%20Town%2C%20contemporary%20suburban%20development%2C%20painters%20in%20clean%20uniforms%20with%20professional%20equipment%2C%20well-planned%20neighborhood%20with%20modern%20architecture%2C%20bright%20natural%20lighting%2C%20high%20quality%20residential%20painting%20project&width=1200&height=600&seq=parklands-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Parklands - Your Residential Estate and Commercial Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With 9 years of dedicated experience serving the Parklands area and northern suburbs, we have established ourselves as the trusted choice for residential estates, commercial properties, and modern homes requiring professional painting services that combine quality craftsmanship with reliable project management.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Residential Estate Painting Specialists</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Parklands is characterized by well-planned residential estates featuring modern townhouses, cluster developments, and apartment complexes that require specialized painting approaches. Our team understands the unique challenges of estate painting, including coordinating with body corporates, managing multiple units simultaneously, and maintaining consistent quality standards across large developments. We work closely with estate managers and homeowner associations to ensure minimal disruption to residents while delivering exceptional results that enhance property values.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Modern Home Interior and Exterior Solutions</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The contemporary architecture prevalent in Parklands requires painting solutions that complement modern design aesthetics while providing practical durability for family living. Our team specializes in current color trends, contemporary finishes, and modern paint technologies that enhance the clean lines and open spaces characteristic of modern homes. We understand how to work with large windows, high ceilings, and open-plan layouts to create cohesive color schemes that maximize natural light and create welcoming living environments.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Commercial Property Expertise</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Parklands hosts numerous commercial developments, office parks, and retail centers that require professional painting services balancing aesthetic appeal with practical durability. Our commercial painting expertise includes understanding building codes, safety regulations, and the specific requirements of different business environments. We work efficiently to minimize business disruption while delivering professional results that enhance corporate image and create positive impressions for customers and employees.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Body Corporate and Strata Painting Services</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Working with body corporates and homeowner associations requires specialized project management skills, clear communication, and the ability to coordinate complex multi-unit projects. Our team has extensive experience managing large-scale estate painting projects, providing detailed quotations, progress reports, and quality assurance documentation that meets the requirements of property management companies and resident committees. We understand the importance of maintaining property values and resident satisfaction throughout the painting process.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Quality Materials and Efficient Project Management</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Our commitment to quality extends beyond craftsmanship to include the use of premium paint systems specifically selected for the northern suburbs climate and the efficient management of projects from initial consultation through final completion. We use advanced project management techniques to ensure timely completion, clear communication with all stakeholders, and consistent quality standards across all aspects of the work. Our material selection focuses on durability, environmental responsibility, and long-term value for property owners.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20modern%20residential%20estate%20home%20in%20Parklands%20Cape%20Town%2C%20northern%20suburbs%20property%20with%20quality%20paint%20finish%2C%20painters%20in%20uniform%20with%20professional%20equipment%2C%20contemporary%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=parklands-sidebar-image&orientation=landscape"
                      alt="Professional painters Parklands residential estate house painting services Cape Town northern suburbs"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Comprehensive Residential and Commercial Painting Services in Parklands</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Estate and Modern Property Painting Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our comprehensive painting services are specifically designed for the residential estates, commercial properties, and modern homes that characterize the Parklands area. From individual townhouses to large apartment complexes and commercial developments, we provide professional solutions that enhance property values while meeting the practical requirements of modern living and business environments.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} services with modern materials and expert application techniques for Parklands properties.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Residential Estate Painting Excellence</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our residential estate painting services are designed to handle the complexities of multi-unit developments, townhouse complexes, and apartment buildings. We understand the importance of coordinating with body corporates, managing resident access, and maintaining consistent quality standards across multiple units while minimizing disruption to daily life.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our project management approach includes detailed scheduling, clear communication protocols, and quality assurance processes that ensure every unit receives the same high standard of workmanship. We work closely with property managers to coordinate access, manage logistics, and provide regular progress updates throughout the project.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Commercial Property Solutions</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our commercial painting services cater to the diverse business environments found throughout Parklands, from office parks and retail centers to industrial facilities and hospitality venues. We understand the importance of maintaining business operations while delivering professional results that enhance corporate image and create positive impressions.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We work efficiently during off-hours when necessary, use low-odor paint systems to minimize disruption, and maintain strict safety protocols to ensure compliance with workplace health and safety requirements. Our commercial expertise includes understanding building codes, fire safety regulations, and the specific durability requirements of different business environments.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Estate and Commercial Projects in Parklands</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent painting projects throughout Parklands and the northern suburbs. Each project demonstrates our expertise in residential estate painting, commercial property enhancement, and modern home transformation with attention to quality and project management excellence.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Parklands Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional residential estate and commercial property painting with modern materials and expert application techniques.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Parklands and the northern suburbs have to say about our painting services. Our commitment to quality workmanship and professional project management is reflected in these genuine reviews from homeowners, body corporates, and business owners.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Parklands</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to enhance your Parklands property with professional painting services? Contact us today for a free consultation and quote. We're here to help you achieve beautiful, lasting results that enhance your residential estate or commercial property.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding northern suburbs</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Estate Assessment</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation assessment for your Parklands residential estate or commercial property painting project. Our experienced team will evaluate your property's specific requirements and provide detailed recommendations.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
